import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery, useInfiniteQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { apiClient } from '@/lib/api'
import { subscribe as subscribeFocusBus } from '@/lib/focusBus'
import type {
  ApiContact,
  ApiCompany,
  ApiDeal,
  ApiQuote,
  ApiJob,
  ApiInvoice,
  ApiSocialPost,
  ApiWorkflow,
  ApiWorkflowExecutionList,
  ApiWorkflowExecution,
  ApiContentIdea,
  ApiReview,
  ApiNotification,
  ApiPhoneNumber,
  ApiRingGroup,
  ApiBusinessHours,
  DashboardStats,
  PaginatedApiResponse,
  ReviewStats,
  RevenueReport,
  PipelineReport,
  JobsReport,
  InvoiceReport,
  InboxThread,
  InboxMessage,
  AttachableResponse,
  UnknownSender,
} from '@/lib/apiTypes'

// ---------------------------------------------------------------------------
// Generic hook
// ---------------------------------------------------------------------------

interface UseApiDataOptions {
  /** How old data must be (ms) before a focus event triggers a refetch. Default 30 s. */
  staleTimeMs?: number
  /** Set true to opt out of focus-refetch entirely (e.g. polling hooks). */
  skipFocusRefetch?: boolean
}

export function useApiData<T>(
  fetcher: () => Promise<T>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deps: any[] = [],
  options: UseApiDataOptions = {}
): { data: T | null; loading: boolean; error: string | null; refetch: () => void } {
  const { staleTimeMs = 30_000, skipFocusRefetch = false } = options
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const lastFetchedAtRef = useRef<number>(0)

  const refetch = useCallback(() => setTick((t) => t + 1), [])

  // Subscribe to the singleton focus bus so returning to the tab (or bfcache
  // restore on iOS Safari) refetches stale data without a full page reload.
  useEffect(() => {
    if (skipFocusRefetch) return
    return subscribeFocusBus(() => {
      if (Date.now() - lastFetchedAtRef.current > staleTimeMs) {
        refetch()
      }
    })
  }, [skipFocusRefetch, staleTimeMs, refetch])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetcher()
      .then((result) => {
        if (!cancelled) {
          setData(result)
          setLoading(false)
          lastFetchedAtRef.current = Date.now()
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message ?? 'Unknown error')
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  return { data, loading, error, refetch }
}

// ---------------------------------------------------------------------------
// Entity params types
// ---------------------------------------------------------------------------

interface ListParams {
  skip?: number
  take?: number
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

interface ContactsParams extends ListParams {
  /** Case-insensitive `tags` membership, filtered server-side. */
  tag?: string
}

interface DealsParams extends ListParams {
  stage?: string
  /** Exclude WON/LOST server-side — the live pipeline, not a single stage. */
  openOnly?: boolean
}

interface StatusParams extends ListParams {
  status?: string
  // Optional window — Calendar view filters jobs by scheduledStart so the
  // page-size cap doesn't hide old-but-scheduled jobs (qa-sweep tier-1a).
  scheduledFrom?: string
  scheduledTo?: string
}

// ---------------------------------------------------------------------------
// Entity hooks — each unwraps the backend envelope { success, data: { items, total } }
// ---------------------------------------------------------------------------

export function useContacts(params: ContactsParams = {}) {
  const result = useQuery<PaginatedApiResponse<ApiContact>, Error>({
    queryKey: ['contacts', 'list', params],
    queryFn: async () => {
      const res = await apiClient.get('/api/contacts', { params })
      return res.data.data as PaginatedApiResponse<ApiContact>
    },
    staleTime: 30_000,
    /**
     * Hold the previous page across a `take` bump only.
     *
     * `take` is part of the key, so "Load more" starts a brand-new query and
     * `isLoading` flips true — which replaced the whole list with a skeleton
     * and threw away the scroll position on every page bump.
     *
     * But holding it across a *filter* change is worse than the skeleton: the
     * previous filter's rows keep rendering under the newly-selected chip, so
     * for the length of the request the page shows contacts tagged
     * `residential` beneath an active "Commercial" chip and says nothing. A
     * placeholder is only honest when the new query is asking for more of the
     * same question — so it is scoped to the paging case and nothing else.
     */
    placeholderData: (previous, previousQuery) => {
      if (!previous) return undefined
      const prevParams = (previousQuery?.queryKey?.[2] ?? {}) as ContactsParams
      // Every field that changes WHICH rows come back, or in what order.
      // `sortBy`/`sortOrder` were missed first time round: holding the old
      // ordering under a newly-chosen sort is the same lie as holding the old
      // filter's rows, just less obvious. No caller sorts today; the point of
      // scoping a shared hook is the caller that does it next.
      const sameQuestion =
        prevParams.tag === params.tag &&
        prevParams.search === params.search &&
        prevParams.sortBy === params.sortBy &&
        prevParams.sortOrder === params.sortOrder
      return sameQuestion ? previous : undefined
    },
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    /** True while a background page/filter change is in flight. */
    fetching: result.isFetching,
    error: result.error?.message ?? null,
    refetch: () => { void result.refetch() },
  }
}

/**
 * Paginated contacts for the Contacts list.
 *
 * The list used to page by growing `take` (50 → 100 → 150…). The API caps
 * `take` at 100 in both the Zod and the Fastify querystring schema, so the
 * third "Load more" was a hard 400 — and because the page tests `error`
 * before rows, that replaced the whole list with
 * "Request failed with status code 400". The commit that introduced it was
 * titled "People list silently truncated at 50, with no way to see the rest":
 * it moved the truncation to 100 and turned it into a visible failure.
 *
 * Real offset pagination has no ceiling and asks for one page at a time, so
 * the cap is never approached.
 */
export function useContactsInfinite(params: Omit<ContactsParams, 'skip' | 'take'> & { pageSize?: number } = {}) {
  const pageSize = params.pageSize ?? 50
  const { search, tag } = params
  const result = useInfiniteQuery<PaginatedApiResponse<ApiContact>, Error>({
    queryKey: ['contacts', 'list-infinite', { search, tag, pageSize }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const res = await apiClient.get('/api/contacts', {
        params: { skip: pageParam as number, take: pageSize, ...(search ? { search } : {}), ...(tag ? { tag } : {}) },
      })
      return res.data.data as PaginatedApiResponse<ApiContact>
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + (p.items?.length ?? 0), 0)
      return loaded < (lastPage.total ?? 0) ? loaded : undefined
    },
    staleTime: 30_000,
  })

  const items = (result.data?.pages ?? []).flatMap((p) => p.items ?? [])
  return {
    items,
    total: result.data?.pages?.[0]?.total ?? 0,
    loading: result.isLoading,
    fetchingMore: result.isFetchingNextPage,
    hasMore: !!result.hasNextPage,
    loadMore: () => { void result.fetchNextPage() },
    error: describeQueryError(result.error),
    refetch: () => { void result.refetch() },
  }
}

export function useContact(contactId: string | null) {
  const result = useQuery<ApiContact, Error>({
    queryKey: ['contacts', 'detail', contactId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/contacts/${contactId}`)
      return (res.data?.data ?? res.data) as ApiContact
    },
    enabled: !!contactId,
    staleTime: 30_000,
    // No `placeholderData` here, deliberately. On a query keyed by a single
    // id it is backwards: while the id is unchanged the key is unchanged and
    // the value is already cached, so it is never consulted; the only time it
    // IS consulted is when the id just changed — exactly when the previous
    // record must not be shown. It bought nothing and cost a drawer that
    // rendered the last record's numbers under the new one's heading with
    // `loading` false. See useApi.detailPlaceholder.test.tsx.
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    // A missing contact is a state, not an error. Passing axios's raw
    // "Request failed with status code 404" through put a developer string in
    // front of anyone following a stale or deleted link, and it also masked
    // the friendly "Contact not found" panel, which only renders when `error`
    // is null. Everything else still surfaces — preferring the server's own
    // message over axios's generic one.
    error: describeQueryError(result.error),
    refetch: () => { void result.refetch() },
  }
}

/**
 * Turn a query failure into something worth showing a user, or null when the
 * component has a better state for it (404 → "not found").
 */
function describeQueryError(err: unknown): string | null {
  if (!err) return null
  const res = (err as { response?: { status?: number; data?: { error?: string; message?: string } } }).response
  if (res?.status === 404) return null
  // A 429 body is the rate limiter's own wording ("Rate limit exceeded, retry
  // in 1 minute"), which reads like a fault the user committed. Say what it
  // means for them instead.
  if (res?.status === 429) return 'Too many requests just now. This will clear in about a minute.'
  // A 5xx body is not written for the person reading it. Whatever threw on
  // the server decided that string — a driver, a plugin, an internal Fastify
  // error — and it can carry a connection string, a file path or query text.
  // A browser sweep forced a 500 with body {"error":"boom"} and watched the
  // dashboard print `boom` to the operator.
  //
  // The backend genericises 5xx messages now, so this is the second line
  // rather than the only one; it holds even against an endpoint that hasn't
  // been routed through that handler, or an older deployment.
  if (typeof res?.status === 'number' && res.status >= 500) {
    return 'Something went wrong on our end. Try again in a moment.'
  }
  return res?.data?.error ?? res?.data?.message ?? (err as Error).message ?? null
}

export function useCompanies(params: ListParams = {}) {
  return useApiData<PaginatedApiResponse<ApiCompany>>(
    async () => {
      const res = await apiClient.get('/api/companies', { params })
      return res.data.data as PaginatedApiResponse<ApiCompany>
    },
    [params.skip, params.take, params.search, params.sortBy, params.sortOrder]
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Work-entity hooks
//
// All four Work entities (deals/quotes/jobs/invoices) follow the same shape
// PR #418 introduced for contacts:
//   list:   ['<entity>', 'list',   params]
//   detail: ['<entity>', 'detail', id]
//   stats:  ['<entity>', 'stats']
//
// invalidateQueries({queryKey: ['<entity>']}) prefix-matches all three. The
// cross-entity helper `invalidateWorkEntities(queryClient)` invalidates every
// node in the Work pipeline at once — use it for any mutation that can change
// stage assignment (stage advances, job convert-to-invoice, quote sign).
// ─────────────────────────────────────────────────────────────────────────────

export const WORK_QUERY_KEYS = {
  deals: {
    all: ['deals'] as const,
    list: (params: DealsParams) => ['deals', 'list', params] as const,
    detail: (id: string) => ['deals', 'detail', id] as const,
  },
  quotes: {
    all: ['quotes'] as const,
    list: (params: StatusParams) => ['quotes', 'list', params] as const,
    detail: (id: string) => ['quotes', 'detail', id] as const,
  },
  jobs: {
    all: ['jobs'] as const,
    list: (params: StatusParams) => ['jobs', 'list', params] as const,
    detail: (id: string) => ['jobs', 'detail', id] as const,
  },
  invoices: {
    all: ['invoices'] as const,
    list: (params: StatusParams) => ['invoices', 'list', params] as const,
    detail: (id: string) => ['invoices', 'detail', id] as const,
    stats: ['invoices', 'stats'] as const,
  },
} as const

export function invalidateWorkEntities(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: WORK_QUERY_KEYS.deals.all })
  void queryClient.invalidateQueries({ queryKey: WORK_QUERY_KEYS.quotes.all })
  void queryClient.invalidateQueries({ queryKey: WORK_QUERY_KEYS.jobs.all })
  void queryClient.invalidateQueries({ queryKey: WORK_QUERY_KEYS.invoices.all })
}

export function useInvalidateWorkEntities(): () => void {
  const queryClient = useQueryClient()
  return useCallback(() => invalidateWorkEntities(queryClient), [queryClient])
}

/** HTTP status behind a query failure, when there was a response at all. */
export function errorStatus(err: unknown): number | null {
  return (err as { response?: { status?: number } })?.response?.status ?? null
}

export function useDeals(params: DealsParams = {}) {
  const result = useQuery<PaginatedApiResponse<ApiDeal>, Error>({
    queryKey: WORK_QUERY_KEYS.deals.list(params),
    queryFn: async () => {
      const res = await apiClient.get('/api/deals', { params })
      return res.data.data as PaginatedApiResponse<ApiDeal>
    },
    staleTime: 30_000,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    status: errorStatus(result.error),
    refetch: () => result.refetch(),
  }
}

export function useDeal(dealId: string | null) {
  const result = useQuery<ApiDeal, Error>({
    queryKey: dealId ? WORK_QUERY_KEYS.deals.detail(dealId) : ['deals', 'detail', null],
    queryFn: async () => {
      const res = await apiClient.get(`/api/deals/${dealId}`)
      return (res.data?.data ?? res.data) as ApiDeal
    },
    enabled: !!dealId,
    staleTime: 30_000,
    // No `placeholderData` here, deliberately. On a query keyed by a single
    // id it is backwards: while the id is unchanged the key is unchanged and
    // the value is already cached, so it is never consulted; the only time it
    // IS consulted is when the id just changed — exactly when the previous
    // record must not be shown. It bought nothing and cost a drawer that
    // rendered the last record's numbers under the new one's heading with
    // `loading` false. See useApi.detailPlaceholder.test.tsx.
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    refetch: () => result.refetch(),
  }
}

export function useQuotes(params: StatusParams = {}) {
  const result = useQuery<PaginatedApiResponse<ApiQuote>, Error>({
    queryKey: WORK_QUERY_KEYS.quotes.list(params),
    queryFn: async () => {
      const res = await apiClient.get('/api/quotes', { params })
      return res.data.data as PaginatedApiResponse<ApiQuote>
    },
    staleTime: 30_000,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    refetch: () => result.refetch(),
  }
}

export function useQuote(quoteId: string | null) {
  const result = useQuery<ApiQuote, Error>({
    queryKey: quoteId ? WORK_QUERY_KEYS.quotes.detail(quoteId) : ['quotes', 'detail', null],
    queryFn: async () => {
      const res = await apiClient.get(`/api/quotes/${quoteId}`)
      return (res.data?.data ?? res.data) as ApiQuote
    },
    enabled: !!quoteId,
    staleTime: 30_000,
    // No `placeholderData` here, deliberately. On a query keyed by a single
    // id it is backwards: while the id is unchanged the key is unchanged and
    // the value is already cached, so it is never consulted; the only time it
    // IS consulted is when the id just changed — exactly when the previous
    // record must not be shown. It bought nothing and cost a drawer that
    // rendered the last record's numbers under the new one's heading with
    // `loading` false. See useApi.detailPlaceholder.test.tsx.
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    refetch: () => result.refetch(),
  }
}

export function useJobs(params: StatusParams = {}) {
  const result = useQuery<PaginatedApiResponse<ApiJob>, Error>({
    queryKey: WORK_QUERY_KEYS.jobs.list(params),
    queryFn: async () => {
      const res = await apiClient.get('/api/jobs', { params })
      return res.data.data as PaginatedApiResponse<ApiJob>
    },
    staleTime: 30_000,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    refetch: () => result.refetch(),
  }
}

export function useJob(jobId: string | null) {
  const result = useQuery<ApiJob, Error>({
    queryKey: jobId ? WORK_QUERY_KEYS.jobs.detail(jobId) : ['jobs', 'detail', null],
    queryFn: async () => {
      const res = await apiClient.get(`/api/jobs/${jobId}`)
      return (res.data?.data ?? res.data) as ApiJob
    },
    enabled: !!jobId,
    staleTime: 30_000,
    // No `placeholderData` here, deliberately. On a query keyed by a single
    // id it is backwards: while the id is unchanged the key is unchanged and
    // the value is already cached, so it is never consulted; the only time it
    // IS consulted is when the id just changed — exactly when the previous
    // record must not be shown. It bought nothing and cost a drawer that
    // rendered the last record's numbers under the new one's heading with
    // `loading` false. See useApi.detailPlaceholder.test.tsx.
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    refetch: () => result.refetch(),
  }
}

export function useInvoices(params: StatusParams = {}) {
  const result = useQuery<PaginatedApiResponse<ApiInvoice>, Error>({
    queryKey: WORK_QUERY_KEYS.invoices.list(params),
    queryFn: async () => {
      const res = await apiClient.get('/api/invoices', { params })
      return res.data.data as PaginatedApiResponse<ApiInvoice>
    },
    staleTime: 30_000,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    status: errorStatus(result.error),
    refetch: () => result.refetch(),
  }
}

export function useInvoice(invoiceId: string | null) {
  const result = useQuery<ApiInvoice, Error>({
    queryKey: invoiceId ? WORK_QUERY_KEYS.invoices.detail(invoiceId) : ['invoices', 'detail', null],
    queryFn: async () => {
      const res = await apiClient.get(`/api/invoices/${invoiceId}`)
      return (res.data?.data ?? res.data) as ApiInvoice
    },
    enabled: !!invoiceId,
    staleTime: 30_000,
    // No `placeholderData` here, deliberately. On a query keyed by a single
    // id it is backwards: while the id is unchanged the key is unchanged and
    // the value is already cached, so it is never consulted; the only time it
    // IS consulted is when the id just changed — exactly when the previous
    // record must not be shown. It bought nothing and cost a drawer that
    // rendered the last record's numbers under the new one's heading with
    // `loading` false. See useApi.detailPlaceholder.test.tsx.
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    refetch: () => result.refetch(),
  }
}

export function useInvoiceStats() {
  const result = useQuery<{ totalPaidRevenue: number }, Error>({
    queryKey: WORK_QUERY_KEYS.invoices.stats,
    queryFn: async () => {
      const res = await apiClient.get('/api/invoices/stats')
      return res.data.data as { totalPaidRevenue: number }
    },
    staleTime: 30_000,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    refetch: () => result.refetch(),
  }
}

export function useDashboardStats(opts?: { from?: string; to?: string }) {
  const result = useQuery<DashboardStats, Error>({
    queryKey: ['dashboardStats', opts?.from, opts?.to],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (opts?.from) params.from = opts.from
      if (opts?.to) params.to = opts.to
      const res = await apiClient.get('/api/dashboard/stats', { params })
      return res.data.data as DashboardStats
    },
    staleTime: 30_000,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    // Callers that ignore this render the zero-valued fallback instead —
    // "$0.00" and "0 deals" on a request that never came back.
    error: describeQueryError(result.error),
    refetch: () => { void result.refetch() },
  }
}

// ---------------------------------------------------------------------------
// Social Media hooks
// ---------------------------------------------------------------------------

interface SocialPostsParams extends ListParams {
  platform?: string
  status?: string
}

export function useSocialPosts(params: SocialPostsParams = {}) {
  return useApiData<PaginatedApiResponse<ApiSocialPost>>(
    async () => {
      const res = await apiClient.get('/api/social/posts', { params })
      return res.data.data as PaginatedApiResponse<ApiSocialPost>
    },
    [params.skip, params.take, params.search, params.platform, params.status, params.sortBy, params.sortOrder]
  )
}

export function useContentIdeas(params: ListParams = {}) {
  return useApiData<PaginatedApiResponse<ApiContentIdea>>(
    async () => {
      const res = await apiClient.get('/api/social/ideas', { params })
      return res.data.data as PaginatedApiResponse<ApiContentIdea>
    },
    [params.skip, params.take]
  )
}

// ---------------------------------------------------------------------------
// Reviews hooks
// ---------------------------------------------------------------------------

interface ReviewsParams extends ListParams {
  platform?: string
  status?: string
  rating?: number
}

export function useReviews(params: ReviewsParams = {}) {
  return useApiData<PaginatedApiResponse<ApiReview>>(
    async () => {
      const res = await apiClient.get('/api/reviews', { params })
      return res.data.data as PaginatedApiResponse<ApiReview>
    },
    [params.skip, params.take, params.platform, params.status, params.rating, params.sortBy, params.sortOrder]
  )
}

export function useReviewStats() {
  return useApiData<ReviewStats>(async () => {
    const res = await apiClient.get('/api/reviews/stats')
    return res.data.data as ReviewStats
  }, [])
}

// ---------------------------------------------------------------------------
// Reports hooks
// ---------------------------------------------------------------------------

interface ReportParams {
  range?: string
  startDate?: string
  endDate?: string
}

export function useRevenueReport(params: ReportParams = {}) {
  return useApiData<RevenueReport>(
    async () => {
      const res = await apiClient.get('/api/reports/revenue', { params })
      return res.data.data as RevenueReport
    },
    [params.range, params.startDate, params.endDate]
  )
}

export function usePipelineReport() {
  return useApiData<PipelineReport>(async () => {
    const res = await apiClient.get('/api/reports/pipeline')
    return res.data.data as PipelineReport
  }, [])
}

export function useJobsReport(params: ReportParams = {}) {
  return useApiData<JobsReport>(
    async () => {
      const res = await apiClient.get('/api/reports/jobs', { params })
      return res.data.data as JobsReport
    },
    [params.range, params.startDate, params.endDate]
  )
}

export function useInvoiceReport() {
  return useApiData<InvoiceReport>(async () => {
    const res = await apiClient.get('/api/reports/invoices')
    return res.data.data as InvoiceReport
  }, [])
}

// ---------------------------------------------------------------------------
// Workflow hooks
// ---------------------------------------------------------------------------

export function useWorkflows(params: ListParams = {}) {
  return useApiData<PaginatedApiResponse<ApiWorkflow>>(
    async () => {
      const res = await apiClient.get('/api/workflows', { params })
      return res.data.data as PaginatedApiResponse<ApiWorkflow>
    },
    [params.skip, params.take, params.search, params.sortBy, params.sortOrder]
  )
}

// Polls every 5 s while any execution is running or paused
export function useWorkflowExecutions(
  workflowId: string | null,
  params: { skip?: number; take?: number } = {}
) {
  const result = useQuery<ApiWorkflowExecutionList, Error>({
    queryKey: ['workflowExecutions', workflowId, params],
    queryFn: async () => {
      const res = await apiClient.get(`/api/workflows/${workflowId}/executions`, { params })
      return res.data.data as ApiWorkflowExecutionList
    },
    enabled: !!workflowId,
    refetchInterval: (query) => {
      const data = query.state.data as ApiWorkflowExecutionList | undefined
      const hasLive = data?.items?.some((e) => e.status === 'running' || e.status === 'paused') ?? false
      return hasLive ? 5000 : false
    },
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    refetch: () => { void result.refetch() },
  }
}

// Polls every 2 s while execution is running or paused, stops on terminal status
export function useWorkflowExecution(execId: string | null) {
  const result = useQuery<ApiWorkflowExecution, Error>({
    queryKey: ['workflowExecution', execId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/workflows/executions/${execId}`)
      return (res.data?.data ?? res.data) as ApiWorkflowExecution
    },
    enabled: !!execId,
    refetchInterval: (query) => {
      const data = query.state.data as ApiWorkflowExecution | undefined
      const isLive = data?.status === 'running' || data?.status === 'paused'
      return isLive ? 2000 : false
    },
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    refetch: () => { void result.refetch() },
  }
}

// ---------------------------------------------------------------------------
// Phone hooks
// ---------------------------------------------------------------------------

export function usePhoneNumbers() {
  return useApiData<ApiPhoneNumber[]>(async () => {
    const res = await apiClient.get('/api/phone/numbers')
    return res.data.data as ApiPhoneNumber[]
  }, [])
}

export function useRingGroups() {
  return useApiData<ApiRingGroup[]>(async () => {
    const res = await apiClient.get('/api/phone/ring-groups')
    return res.data.data as ApiRingGroup[]
  }, [])
}

export function useBusinessHours() {
  return useApiData<ApiBusinessHours>(async () => {
    const res = await apiClient.get('/api/phone/business-hours')
    return res.data.data as ApiBusinessHours
  }, [])
}

// ---------------------------------------------------------------------------
// Notification hooks
// ---------------------------------------------------------------------------

export function useNotifications() {
  return useApiData<{ items: ApiNotification[]; total: number }>(
    async () => {
      const res = await apiClient.get('/api/notifications/recent')
      return res.data.data as { items: ApiNotification[]; total: number }
    },
    []
  )
}

// ---------------------------------------------------------------------------
// Inbox hooks
// ---------------------------------------------------------------------------

// Scoped retry policy for inbox queries: if the session expires mid-tab
// (token TTL, server-side revoke), the global axios interceptor already
// triggers the /login redirect — but TanStack Query's default 3-retry policy
// would otherwise hammer the endpoint while the redirect navigates. Skip
// retry on 401 entirely so the redirect handler can run unimpeded. Bound
// the retry to ONE attempt for transient 5xx/network so a flapping connection
// doesn't multiply badge polls. Scoped to inbox to avoid cascading effects
// on Work tab and other surfaces (per scope note in FOR Wave 3).
function inboxRetry(failureCount: number, error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    if (status === 401 || status === 403) return false
    if (status && status >= 400 && status < 500) return false
  }
  return failureCount < 1
}

export function useInboxThreads(channel?: string) {
  const result = useQuery<{ items: InboxThread[]; total: number; unreadCount: number; unknownSenders: UnknownSender[] }, Error>({
    queryKey: ['inboxThreads', channel ?? 'all'],
    queryFn: async () => {
      const params = channel && channel !== 'all' ? { channel } : {}
      const res = await apiClient.get('/api/inbox/threads', { params })
      const data = res.data.data as { items: InboxThread[]; total: number; unreadCount: number; unknownSenders?: UnknownSender[] }
      return { ...data, unknownSenders: data.unknownSenders ?? [] }
    },
    staleTime: 10_000,
    retry: inboxRetry,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    refetch: () => { void result.refetch() },
  }
}

export function useInboxStats() {
  const result = useQuery<{ unreadCount: number }, Error>({
    queryKey: ['inboxStats'],
    queryFn: async () => {
      const res = await apiClient.get('/api/inbox/threads', { params: { take: 1 } })
      const data = res.data.data as { unreadCount: number }
      return { unreadCount: data.unreadCount ?? 0 }
    },
    staleTime: 10_000,
    retry: inboxRetry,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    refetch: () => { void result.refetch() },
  }
}

export function useInboxThread(contactId: string | null) {
  const result = useQuery<{ contact: Record<string, unknown>; messages: InboxMessage[] }, Error>({
    queryKey: ['inboxThread', contactId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/inbox/threads/${contactId}`)
      return res.data.data as { contact: Record<string, unknown>; messages: InboxMessage[] }
    },
    enabled: !!contactId,
    staleTime: 10_000,
    retry: inboxRetry,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    refetch: () => { void result.refetch() },
  }
}

export function useContactAttachable(contactId: string | null) {
  const result = useQuery<AttachableResponse, Error>({
    queryKey: ['contactAttachable', contactId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/inbox/contacts/${contactId}/attachable`)
      return res.data.data as AttachableResponse
    },
    enabled: !!contactId,
    staleTime: 30_000,
    retry: inboxRetry,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error?.message ?? null,
    refetch: () => { void result.refetch() },
  }
}
